import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/Users/rafaelosorio/Documents/GitHub/cp4i-demohub/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`In this lab, you will secure the Inventory API to protect the resources
exposed by `}<strong parentName="p">{`API Connect`}</strong>{`. Consumers of your API will be required to
obtain and provide a valid OAuth token before they can invoke the
Inventory API.`}</p>
    <p>{`In this tutorial, you will explore the following key capabilities:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Configure an OAuth 2.0 service, the Resource Owner Password grant
type.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Clone a new version of an API.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Secure the new version of your API.`}</p>
      </li>
    </ul>
    <h2>{`APIC Dev Jam Series`}</h2>
    <p>{`The APIC Dev Jam Series is a hands-on workshop with lab exercises that
walk you through designing, publishing, and securing APIs. This workshop
is for API developers, architects, and line of business people who want
to create a successful API strategy. There are 8 labs and each is 30
minutes long. Make sure you choose enough time in your reservation to
get through all the labs! `}</p>
    <p>{`[NOTE: ]`}<strong parentName="p">{`[This demo environment contains a
full API Connect installation in Cloud Pak for Integration. The login
information to the APIC cluster will be sent in a separate email when
you reserve the instance. Use Google Chrome, Firefox or Microsoft Edge
to access the cluster using the credentials supplied. Make sure you
login using API Manager User Registry not Common Services
registry.]`}</strong></p>
    <p>{`Prerequisites: Labs 1-2`}</p>
    <h2>{`Configure a New OAuth 2.0 Provider API`}</h2>
    <p>{`API Connect is a full-featured OAuth 2.0 provider. The OAuth exchange
works like any other API call, and thus we treat it as its own API. In
this section, you will create a new OAuth provider API, configure which
grant type to use, and configure how it will authenticate user
credentials.`}</p>
    <h3>{`Configure Authentication URL User Registry`}</h3>
    <p>{`In order to configure user authentication, you must first define the
registry to use, which may be LDAP, local user registry, or an
authentication URL. For our lab, we will implement an Authentication
URL.`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`In the API Manager from the main menu on the left,
click [`}{`[Resources]`}{`].`}</p>
        <span parentName="li" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "38.541666666666664%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAICAYAAAD5nd/tAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA/0lEQVQoz61QQWrEMAz0e9skEMiv+oAe+4D+YCm97JKExTRWHNtZx87UcuuSPbZUMEgeS6NBous6tG2LpmlQVRXquv41qqrGw+NXFkjhnMN/hZg+CJfTCbLvcZUSappgrc1Ltm2D9/4OR+6WgOjxfg14eiH0ZwmhiTAOA0gpUKq11hjHATKJz/OcOc4FU1pYOKIZZiG8XQjPr5QMaYi477lhWZYMdqdmC6ksJjLpbWCMyX+cVVrMolxnPuG2muTUYnUWYv8WZGcMFjTOQ5sNq48IYcu34T6OGONPzRFCSGcIYIrPJLjhKFicsLPijpcU8NDxXZxyzbN3gkwU4b/iE4mXS8u3dnNmAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "tutorial html newcreateregistry",
            "title": "tutorial html newcreateregistry",
            "src": "/cp4i-demohub/static/804a7106c5826ea884a4ff3bbfd963a0/3cbba/tutorial_html_newcreateregistry.png",
            "srcSet": ["/cp4i-demohub/static/804a7106c5826ea884a4ff3bbfd963a0/7fc1e/tutorial_html_newcreateregistry.png 288w", "/cp4i-demohub/static/804a7106c5826ea884a4ff3bbfd963a0/a5df1/tutorial_html_newcreateregistry.png 576w", "/cp4i-demohub/static/804a7106c5826ea884a4ff3bbfd963a0/3cbba/tutorial_html_newcreateregistry.png 1152w", "/cp4i-demohub/static/804a7106c5826ea884a4ff3bbfd963a0/0b124/tutorial_html_newcreateregistry.png 1728w", "/cp4i-demohub/static/804a7106c5826ea884a4ff3bbfd963a0/4ea69/tutorial_html_newcreateregistry.png 2304w", "/cp4i-demohub/static/804a7106c5826ea884a4ff3bbfd963a0/a7081/tutorial_html_newcreateregistry.png 2462w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Select the [`}{`[Authentication URL User
Registry]`}{`] tile.`}</p>
        <span parentName="li" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "34.72222222222222%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsTAAALEwEAmpwYAAAA3UlEQVQoz6VR0W7DIBDj/z+zW9tACgNyEEKUeXckTKkqbQ9FsgDrzj6DeriIcbTw/gtaa6SU8M5Sec6YiBpCnJBLQa0rlmV5Qa31X1652xX64wLDcMMd0WhMzu4mExvk3EB8jzE+cXLukGTCqWztXsANiUHeowSPxNw5vrjLvXCCvuZ5/jXbtq1BpdEgiAC7FC745vEzT5j5LCLStK5rEwshNHSuTyt1YiScGj/vGBhuGBCMQWSDaPfIdEDEpFnEJHbn+hOc69TDZ7ggH5NA0pj2nY6PouMt6WTwF/cDWqweOcR40iwAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "tutorial html ae58ee320d642047",
            "title": "tutorial html ae58ee320d642047",
            "src": "/cp4i-demohub/static/1ba5b8ea9bbe0d450bb198b80ffd925b/3cbba/tutorial_html_ae58ee320d642047.png",
            "srcSet": ["/cp4i-demohub/static/1ba5b8ea9bbe0d450bb198b80ffd925b/7fc1e/tutorial_html_ae58ee320d642047.png 288w", "/cp4i-demohub/static/1ba5b8ea9bbe0d450bb198b80ffd925b/a5df1/tutorial_html_ae58ee320d642047.png 576w", "/cp4i-demohub/static/1ba5b8ea9bbe0d450bb198b80ffd925b/3cbba/tutorial_html_ae58ee320d642047.png 1152w", "/cp4i-demohub/static/1ba5b8ea9bbe0d450bb198b80ffd925b/0b124/tutorial_html_ae58ee320d642047.png 1728w", "/cp4i-demohub/static/1ba5b8ea9bbe0d450bb198b80ffd925b/0c220/tutorial_html_ae58ee320d642047.png 2218w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Specify the only following properties and then
click [`}{`[Save.]`}{`]`}</p>
        <p parentName="li">{`   Title: [`}{`[App Registry]`}{`]`}</p>
        <p parentName="li">{`   URL: `}<a parentName="p" {...{
            "href": "https://thinkibm-services.mybluemix.net/auth"
          }}>{`https://thinkibm-services.mybluemix.net/auth`}</a></p>
        <p parentName="li">{`   Display name:[`}{`[ App Registry]`}{`]`}</p>
        <p parentName="li">{`   Click `}<strong parentName="p">{`Save`}</strong>{` to save the resource`}</p>
        <p parentName="li">{`   `}<span parentName="p" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1152px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "77.43055555555556%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAPCAYAAADkmO9VAAAACXBIWXMAABYlAAAWJQFJUiTwAAAA/UlEQVQ4y62TSU7EMBBFfUtW3AkEV2kOw7ZZJESZ2o4z55Nn5IhlBiw9lRPFP+VfVaZtW3nvRey6TvF5nmexlmXR3sW3xlqrLMvUNM0mSET0CPG8GYYhbIhXGcdRJmZDvAqihiuS7jRN/4JxzilJEhFjQc7S970Mm7quwws8uOwhqn9bJvpxJtuQIQe5Lj4Ce0CUPx7FIFIUhcqyVFVVG9HTI2weIkCD4wMTAlTsdIZkh+CVKlu3TovvaGw8/BVjFo8yh1kHZn/twzRvlFdO1YPCuK0oe7D47J2+vhu93nq9fawZ3lOnNHdBlEy5+l4eK523+rw7Pb0Men4f9AMlE47K3QbZoAAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "tutorial html ae75a185a7c7e950",
              "title": "tutorial html ae75a185a7c7e950",
              "src": "/cp4i-demohub/static/de7a4ddb4c26fe3b6bb0a3e5aa4bd8f4/3cbba/tutorial_html_ae75a185a7c7e950.png",
              "srcSet": ["/cp4i-demohub/static/de7a4ddb4c26fe3b6bb0a3e5aa4bd8f4/7fc1e/tutorial_html_ae75a185a7c7e950.png 288w", "/cp4i-demohub/static/de7a4ddb4c26fe3b6bb0a3e5aa4bd8f4/a5df1/tutorial_html_ae75a185a7c7e950.png 576w", "/cp4i-demohub/static/de7a4ddb4c26fe3b6bb0a3e5aa4bd8f4/3cbba/tutorial_html_ae75a185a7c7e950.png 1152w", "/cp4i-demohub/static/de7a4ddb4c26fe3b6bb0a3e5aa4bd8f4/dd65b/tutorial_html_ae75a185a7c7e950.png 1640w"],
              "sizes": "(max-width: 1152px) 100vw, 1152px",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              },
              "loading": "lazy"
            }}></img>{`
    `}</span></p>
        <p parentName="li">{`Create OAuth Service`}</p>
      </li>
    </ol>
    <hr></hr>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`In the API Manager from the main menu on the left,
click [`}{`[Resources]`}{`].`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`In the Resources menu, click  [`}{`[OAuth Providers-`}{`>`}{` Native OAuth
Provider]`}{`].`}</p>
        <span parentName="li" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "35.06944444444445%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsTAAALEwEAmpwYAAAA8UlEQVQoz4VR7WqEMBDM+z9J/5Y+RKH3BvFAELGQRs+cn9HoNBO6Qe5PF4aJZnZ2s6vatkVd17DWwnufsW1b4nVd/wV1QMTpoeZpwjAMWJYF8zxnTPE/hcdxIISQ+BUhccDmN3zcPL50NKwbA10UaJoGxpjUad/3mae/gsQ4jpkTeNdamHuBt/c7Pm/fUM490XWPlOycSwk8d12XWYx4zxGJVu5b+xNf/ATCAkUxu8hVI87zxDXKskRVVWkE1MpIOGeJI6bs4YCS51yfxZkxxFhrnRZHA5pxEfu+pzleg9/q2pkYypZl0xI0YTEyIZqr9hfmuxyeyNv/CgAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "tutorial html f1fe85d169c1b8fc",
            "title": "tutorial html f1fe85d169c1b8fc",
            "src": "/cp4i-demohub/static/5f7e560dc74035499544b0cdd68c27fa/3cbba/tutorial_html_f1fe85d169c1b8fc.png",
            "srcSet": ["/cp4i-demohub/static/5f7e560dc74035499544b0cdd68c27fa/7fc1e/tutorial_html_f1fe85d169c1b8fc.png 288w", "/cp4i-demohub/static/5f7e560dc74035499544b0cdd68c27fa/a5df1/tutorial_html_f1fe85d169c1b8fc.png 576w", "/cp4i-demohub/static/5f7e560dc74035499544b0cdd68c27fa/3cbba/tutorial_html_f1fe85d169c1b8fc.png 1152w", "/cp4i-demohub/static/5f7e560dc74035499544b0cdd68c27fa/0b124/tutorial_html_f1fe85d169c1b8fc.png 1728w", "/cp4i-demohub/static/5f7e560dc74035499544b0cdd68c27fa/a825a/tutorial_html_f1fe85d169c1b8fc.png 2172w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Specify the following properties and
click `}<strong parentName="p">{`[Next]`}</strong>{` to continue.`}</p>
        <p parentName="li">{`Title: [`}{`[oauth]`}{`]`}</p>
        <p parentName="li">{`Name: [`}{`[oauth]`}{`]`}</p>
        <p parentName="li">{`Gateway Type: [`}{`[DataPower API Gateway]`}{`]`}</p>
        <span parentName="li" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "73.26388888888889%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAPCAYAAADkmO9VAAAACXBIWXMAABYlAAAWJQFJUiTwAAABHklEQVQ4y62TSU7FQAxEcziOxp4LsOAQLBELxBEQRyDK/xl6TM8p7M4HBBISSWjpqTftSsVlN33fYxgG8K2UwrIsKKXsIoSAJsaInPMnLLiXKjhNE5xzlXmed8P13ns0rMouj5JSWgW9d2TVH4OEIhmrgkIaCGXgfEKI+QCFNKiHgwyYlCfCATxGuo0lh8ZoGGNqD9ak0y5KyWvKlhKylrHf4OTsH6jvLu+dI4f4x8NpN23bouu6+hX+7Y8x4rNlqPlcBltACEZCyhUW3CKWy4KUQWNDgv35BEHbMo5j7QO73OKuFHZXuII90hwqB2V47b5W72dAv6ENtSkYPL043NwHPL/OaN5OGudRQyoNrbfBNUvUuH1wuLpOuHt0eAetJ5IDVHrJywAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "tutorial html 22f9d0d5c30f657d",
            "title": "tutorial html 22f9d0d5c30f657d",
            "src": "/cp4i-demohub/static/504c9a8a9012bb483a9d592481d98ffe/3cbba/tutorial_html_22f9d0d5c30f657d.png",
            "srcSet": ["/cp4i-demohub/static/504c9a8a9012bb483a9d592481d98ffe/7fc1e/tutorial_html_22f9d0d5c30f657d.png 288w", "/cp4i-demohub/static/504c9a8a9012bb483a9d592481d98ffe/a5df1/tutorial_html_22f9d0d5c30f657d.png 576w", "/cp4i-demohub/static/504c9a8a9012bb483a9d592481d98ffe/3cbba/tutorial_html_22f9d0d5c30f657d.png 1152w", "/cp4i-demohub/static/504c9a8a9012bb483a9d592481d98ffe/7cda5/tutorial_html_22f9d0d5c30f657d.png 1666w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </li>
      <li parentName="ol">
        <p parentName="li">{`The next configuration screen will display the default paths to the
Authorize and Token functions. For Supported grant types,
choose [`}{`[Resource owner
password]`}{`].
For Supported Client types,
choose [`}{`[Confidential]`}{`].
Click [`}{`[Next]`}{`] continue.`}</p>
        <span parentName="li" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "79.8611111111111%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAQCAYAAAAWGF8bAAAACXBIWXMAABYlAAAWJQFJUiTwAAABI0lEQVQ4y6VTwU6EMBTkI/0JT5v4Ed6Nv4DxZEyM571svC0X795MUIFSaCktUEYGs2SNFykvmTQPwnRm3iPSWiPLMlRVhXEc4b0PAr/tug6RMQZ1Xc9NKNkJfd8japoGeZ6jKApQLftQtG37QyilhLUWwzBswmKZ6mibN2xWSJtUWJblTOycA4shrwFrzpBWifNphWAhVEpBCDFl0CO0fhF+CotCSLhJZYjVPwrTNF0Wm1lyUpsy5JQ5IZ7M8vQy2DKJqI5rwyxtoPWFkOvCwbBZS8StmJeapwdcR8JJlZxIvR+CbJ49oUZElTLQjYXSZtVfwWiOSYLnp0e8JG+IDx77V4Po/UvjI1coSjVb/w+YNwnju3tc7S5xGx9wcQNcPxh8A29e4MFqVG6hAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "tutorial html 2e278c6df90a639f",
            "title": "tutorial html 2e278c6df90a639f",
            "src": "/cp4i-demohub/static/86533c0c9c79282b8fdbf2bc2ae15dff/3cbba/tutorial_html_2e278c6df90a639f.png",
            "srcSet": ["/cp4i-demohub/static/86533c0c9c79282b8fdbf2bc2ae15dff/7fc1e/tutorial_html_2e278c6df90a639f.png 288w", "/cp4i-demohub/static/86533c0c9c79282b8fdbf2bc2ae15dff/a5df1/tutorial_html_2e278c6df90a639f.png 576w", "/cp4i-demohub/static/86533c0c9c79282b8fdbf2bc2ae15dff/3cbba/tutorial_html_2e278c6df90a639f.png 1152w", "/cp4i-demohub/static/86533c0c9c79282b8fdbf2bc2ae15dff/cd87c/tutorial_html_2e278c6df90a639f.png 1678w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </li>
      <li parentName="ol">
        <p parentName="li">{`One scope is generated for you
: [`}<strong parentName="p">{`[sample_scope_1]`}</strong>{`[.]`}{`]`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Modify the values
for `}<strong parentName="p">{`[sample_scope_1]`}</strong>{`, set the
following fields:`}</p>
        <p parentName="li">{`Name: [`}{`[inventory]`}{`]`}</p>
        <p parentName="li">{`Description: [`}{`[Access to Inventory API]`}{`]`}</p>
        <span parentName="li" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "51.73611111111111%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAKCAYAAAC0VX7mAAAACXBIWXMAACHVAAAh1QEEnLSdAAAArklEQVQoz62S3Q6DIAyFef8n21Psbt5smQq0FNxyVohOkynOxIuPlv4cCsFcGkEkj9Z6eAqI6YU0ZN6jnZE44N5xqVmDZYBJEvHsHYgFQRtY95neEbzGWBI4RIRiBc3DaTwpcRXjdGktw3qZIUFnSeHi51gWz3XXW1cOyc0U0g9mK8ErsTyZozj6G4JT8h8o1MW+E9YKlgLL2uqEZ3Ka4DSx2bvuEbHybfYe+ajoB/CICe0kbN99AAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "tutorial html b42ee8bacaf23a4c",
            "title": "tutorial html b42ee8bacaf23a4c",
            "src": "/cp4i-demohub/static/7048921d5904261ee604de4b505f6aec/3cbba/tutorial_html_b42ee8bacaf23a4c.png",
            "srcSet": ["/cp4i-demohub/static/7048921d5904261ee604de4b505f6aec/7fc1e/tutorial_html_b42ee8bacaf23a4c.png 288w", "/cp4i-demohub/static/7048921d5904261ee604de4b505f6aec/a5df1/tutorial_html_b42ee8bacaf23a4c.png 576w", "/cp4i-demohub/static/7048921d5904261ee604de4b505f6aec/3cbba/tutorial_html_b42ee8bacaf23a4c.png 1152w", "/cp4i-demohub/static/7048921d5904261ee604de4b505f6aec/4147f/tutorial_html_b42ee8bacaf23a4c.png 1429w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Click [`}{`[Next]`}{`].`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Keep all items default.
Click [`}{`[Next.]`}{`]`}</p>
        <span parentName="li" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "83.68055555555556%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAARCAYAAADdRIy+AAAACXBIWXMAABYlAAAWJQFJUiTwAAAA5klEQVQ4y7WUyw6CMBBF+/UuSIx/4sLPwegGQVTkVd4Pr96axroTiiQnbSbMyQztIMIwhOd5CIIAvu+rfZZlqOsaVVVNRpiJXdeBzziOGIZhEszp+x6iLEtVDWmaRknnQBlX0bbtV8AGugSrIlpsLdStLibUFdrK/ifULS92KBTqq7NYy6aMQRvUxS6KQkExb/xc1KRw5CjlSiGDc9AdKqE5k1Mk5vuPB17rAMG/TBTdkCQJ4jiejM47Hg+IblcIKd/fz+Zk8zzHKbjgnhYQ3qVCnH4O5leklErounusnRU22zOcHfAEdUMs8YDNuG4AAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "tutorial html 985e9dbc3a3f82c0",
            "title": "tutorial html 985e9dbc3a3f82c0",
            "src": "/cp4i-demohub/static/0a31f6be145c8a2002563aba2cc1c47c/3cbba/tutorial_html_985e9dbc3a3f82c0.png",
            "srcSet": ["/cp4i-demohub/static/0a31f6be145c8a2002563aba2cc1c47c/7fc1e/tutorial_html_985e9dbc3a3f82c0.png 288w", "/cp4i-demohub/static/0a31f6be145c8a2002563aba2cc1c47c/a5df1/tutorial_html_985e9dbc3a3f82c0.png 576w", "/cp4i-demohub/static/0a31f6be145c8a2002563aba2cc1c47c/3cbba/tutorial_html_985e9dbc3a3f82c0.png 1152w", "/cp4i-demohub/static/0a31f6be145c8a2002563aba2cc1c47c/8cdd5/tutorial_html_985e9dbc3a3f82c0.png 1676w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Review your OAuth configuration and
click [`}{`[Finish]`}{`].
Then click on the back arrow.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`From the Sandbox Catalog registry setting, select API User
Registries and Add App Registry. To open Sandbox Settings follow
Home-`}{`>`}{`Manage Catalogs-`}{`>`}{`Sandbox-`}{`>`}{`Settings(Gearbox)->API User Registries.`}</p>
        <p parentName="li">{`   Use `}<strong parentName="p">{`Edit`}</strong>{` option and enable available API registry.`}</p>
        <p parentName="li">{`   `}<span parentName="p" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1152px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "45.833333333333336%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAJCAYAAAAywQxIAAAACXBIWXMAABYlAAAWJQFJUiTwAAABMUlEQVQoz5VSUU6EMBTkqB7Bc/hr4rdH8MuTmIgmJCALC6UtC20pFHbHvpIl+GF0XzLpMPRN5xUiYx1qoVHXFbho0bQWSmlYO2AcR9xa0TAYHI+FN6xRVRWYN86yDHmegzGGy+XybwTDaZpAdFkWnM/nbb1y51wA7duDtHmef2jWWkR93+Mjfg+JkiTB4XBAURTbSmnTNPVTHMMzoSzLoMVxvGk0jTEGkdIan0kOKWUYu2katG27gXMedFqFEJtOnPZKzxsuIU89zDAgopiis2F+59bY+6K7oY9DuN7Tb+VGn1CeNGRnwFsNpU2IrZTaoP0EpBGI798ZrSB83+PriIeXEUnpDZn/Zb4qjYprdL032DX8hcEoMKlx/zzh7snhLfWG66kriN+CtUcBs8eyHvANwQWx+cbsr3QAAAAASUVORK5CYII=')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "tutorial html c24f06de482a8ab5",
              "title": "tutorial html c24f06de482a8ab5",
              "src": "/cp4i-demohub/static/a705f31e6ebf9e5395af2728a8676f3a/3cbba/tutorial_html_c24f06de482a8ab5.png",
              "srcSet": ["/cp4i-demohub/static/a705f31e6ebf9e5395af2728a8676f3a/7fc1e/tutorial_html_c24f06de482a8ab5.png 288w", "/cp4i-demohub/static/a705f31e6ebf9e5395af2728a8676f3a/a5df1/tutorial_html_c24f06de482a8ab5.png 576w", "/cp4i-demohub/static/a705f31e6ebf9e5395af2728a8676f3a/3cbba/tutorial_html_c24f06de482a8ab5.png 1152w", "/cp4i-demohub/static/a705f31e6ebf9e5395af2728a8676f3a/b00f0/tutorial_html_c24f06de482a8ab5.png 1286w"],
              "sizes": "(max-width: 1152px) 100vw, 1152px",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              },
              "loading": "lazy"
            }}></img>{`
    `}</span></p>
        <p parentName="li">{`Add the OAuth Service to the Sandbox Catalog`}</p>
      </li>
    </ol>
    <hr></hr>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`From the left menu, click [`}{`[Home Button
Icon]`}{`].`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Click the [`}{`[Manage
Catalog]`}{`] catalog
and select
[`}{`[Sandbox]`}{`] `}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`From the left menu,
click [`}{`[Settings]`}{`]
gearbox.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`On the displayed menu, click [`}{`[OAuth
Providers]`}{`].`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Click [`}{`[Edit]`}{`].`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Choose the OAuth service created above. Then
click [`}{`[Save]`}{`].
Click on the back arrow to go back to Manage.`}</p>
        <span parentName="li" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "30.208333333333332%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAGCAYAAADDl76dAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAxUlEQVQY06WPbYrCMBCGc1JPtYdZ8AqysH8XhJqkiautbTKZidqy78YKIqIiOPAwn7wzo5ouwVgHv97Cb3aIMeIdUyI8iaREIIoFwjiOGIbhKaeZe3NK6xWstfDeX6iqCnVdP8Q5B2MMtNZTfKppY9G2O6iV3ZQLCTkLRM70fQ9mvuS35JynT0IIJc847AX4k+ITVNMx+ijg0rgWfQVmwbGI+SZj/n3A0gnUukn4bQldICSiafOrhPLZsCcsfhizjyM+vwT/TUDL7VUdrX8AAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "tutorial html 6fa9961893476e8e",
            "title": "tutorial html 6fa9961893476e8e",
            "src": "/cp4i-demohub/static/c35cfda348f6a4efa506c859845bc93c/3cbba/tutorial_html_6fa9961893476e8e.png",
            "srcSet": ["/cp4i-demohub/static/c35cfda348f6a4efa506c859845bc93c/7fc1e/tutorial_html_6fa9961893476e8e.png 288w", "/cp4i-demohub/static/c35cfda348f6a4efa506c859845bc93c/a5df1/tutorial_html_6fa9961893476e8e.png 576w", "/cp4i-demohub/static/c35cfda348f6a4efa506c859845bc93c/3cbba/tutorial_html_6fa9961893476e8e.png 1152w", "/cp4i-demohub/static/c35cfda348f6a4efa506c859845bc93c/cd87c/tutorial_html_6fa9961893476e8e.png 1678w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </li>
    </ol>
    <h2>{`Create a New Version of the Inventory API`}</h2>
    <p>{`API Connect supports multiple versions of APIs. Create a new version of
the inventory API before making any changes that would break
functionality for existing consumers. `}</p>
    <h3>{`[Save as a New Version]`}</h3>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`In the API Manager from the main menu on the left,
click [`}{`[Develop]`}{`].`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Click on the menu icon to the right of [`}{`[inventory
1.0.0]`}{`] API
and select [`}{`[Save as a new
version]`}{`].  `}</p>
        <span parentName="li" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "31.944444444444443%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAGCAYAAADDl76dAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAwElEQVQY051QWw6CMBDk/iGgEbyJnkK9gJqghkfT1ipQpS0w0saQEL9wkslOdiez23qrKIaUNYQQMMZAKTWLTaMAKGz3CsuNghfFa1hIKYdh40xa6xk0rr7eGpUcAn3fR1lJZFkGQggopRMyxpCm6aitrygKcM5dvSSJ6zNGIe4cXhgu3IV6eG7f9+i67odt247afov5em3faipqkHsJ/qzhBUHgAu3wX9yOZ5wOO+TXbBpot86lBWUPkJyAD1d+ABp4ytqvJNUxAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "tutorial html 4f0d083547b597a4",
            "title": "tutorial html 4f0d083547b597a4",
            "src": "/cp4i-demohub/static/1a376a3d6c026eb66274335faa56561d/3cbba/tutorial_html_4f0d083547b597a4.png",
            "srcSet": ["/cp4i-demohub/static/1a376a3d6c026eb66274335faa56561d/7fc1e/tutorial_html_4f0d083547b597a4.png 288w", "/cp4i-demohub/static/1a376a3d6c026eb66274335faa56561d/a5df1/tutorial_html_4f0d083547b597a4.png 576w", "/cp4i-demohub/static/1a376a3d6c026eb66274335faa56561d/3cbba/tutorial_html_4f0d083547b597a4.png 1152w", "/cp4i-demohub/static/1a376a3d6c026eb66274335faa56561d/0b124/tutorial_html_4f0d083547b597a4.png 1728w", "/cp4i-demohub/static/1a376a3d6c026eb66274335faa56561d/1192c/tutorial_html_4f0d083547b597a4.png 2150w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Enter the new version number
as [`}{`[2.0.0]`}{`] and
click [`}{`[Submit]`}{`].`}</p>
      </li>
    </ol>
    <h2>{`Add OAuth security to the Inventory API`}</h2>
    <p>{`Modify the security policy for your new API version to tell it to use
your OAuth 2.0 provider.`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`From the Develop home page, click `}{`\``}<strong parentName="p">{`Inventory 2.0.0`}</strong>{`\``}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Navigate to the `}<strong parentName="p">{`[Security
Definitions]`}</strong>{` section.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Click [`}{`[Add]`}{`].`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`On the API Security Definition screen, enter the following:`}</p>
        <ul parentName="li">
          <li parentName="ul">
            <p parentName="li">{`Name: `}<strong parentName="p">{`[oauth-1]`}</strong></p>
          </li>
          <li parentName="ul">
            <p parentName="li">{`Description: [`}{`[API OAuth security
definition]`}{`]`}</p>
          </li>
          <li parentName="ul">
            <p parentName="li">{`Type: [`}{`[OAuth2]`}{`]`}</p>
          </li>
          <li parentName="ul">
            <p parentName="li">{`Flow: [`}{`[Resource owner]`}{`]`}</p>
          </li>
          <li parentName="ul">
            <p parentName="li">{`Token URL: keep
default [`}{`[https://\\$(catalog.url)/oauth/oauth2/token]`}{`]`}</p>
          </li>
          <li parentName="ul">
            <p parentName="li">{`Leave everything else to the default values and
click `}<strong parentName="p">{`Save`}</strong>{`.  `}</p>
            <span parentName="li" {...{
              "className": "gatsby-resp-image-wrapper",
              "style": {
                "position": "relative",
                "display": "block",
                "marginLeft": "auto",
                "marginRight": "auto",
                "maxWidth": "1152px"
              }
            }}>{`
      `}<span parentName="span" {...{
                "className": "gatsby-resp-image-background-image",
                "style": {
                  "paddingBottom": "86.1111111111111%",
                  "position": "relative",
                  "bottom": "0",
                  "left": "0",
                  "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAARCAYAAADdRIy+AAAACXBIWXMAABYlAAAWJQFJUiTwAAABcklEQVQ4y6VUS26DMBTkvD1Ur9JFL9BN1V0UBaSEjwlgfraBqecRp6irQi2NnvET4/cbR3Vd43a74Xq9ii2KAlVVYZomzPO8G1Hf9+i6DrTEsizgot0DLuccIpIxSmstjDEYhuEw+L9E2DSNYBxHOTwKBhWRuW1baK2fN4X094CZMqCIZCQhOx3c761fqCEbKRGSmYQh5aNLCBkVm0IyHjg3CfkRGGNJ6EfG52+t8aRrtOYo2OVaD0hVi/zeodL2MFRjoTsfYdcPKO+N//DRGXcYw0jYdbCzLIVSCqWH8tKr60rSl4b9AeuoPQabhGVZCiHBJlGTlNHaJCeN4n4Lt/Et88QeA7P7GZv/rM8EeHkF3r/82FAhaZqKWoIEAzhOWZbJ6/Pbv/pytLpBkjZ4+6gQZ/2qlMvlgiRJ5PkiAS8g8jzH6XTC+XxGHMfyxAU/fWJ9zVWRY2wVzNAhYh2CnrePA/esJe1W61vfUyGP/TTN+AZhZCsGIzeMkAAAAABJRU5ErkJggg==')",
                  "backgroundSize": "cover",
                  "display": "block"
                }
              }}></span>{`
  `}<img parentName="span" {...{
                "className": "gatsby-resp-image-image",
                "alt": "tutorial html 9b9f57dc81561ae7",
                "title": "tutorial html 9b9f57dc81561ae7",
                "src": "/cp4i-demohub/static/010360d30aa6c703b0063c05a86b083b/3cbba/tutorial_html_9b9f57dc81561ae7.png",
                "srcSet": ["/cp4i-demohub/static/010360d30aa6c703b0063c05a86b083b/7fc1e/tutorial_html_9b9f57dc81561ae7.png 288w", "/cp4i-demohub/static/010360d30aa6c703b0063c05a86b083b/a5df1/tutorial_html_9b9f57dc81561ae7.png 576w", "/cp4i-demohub/static/010360d30aa6c703b0063c05a86b083b/3cbba/tutorial_html_9b9f57dc81561ae7.png 1152w", "/cp4i-demohub/static/010360d30aa6c703b0063c05a86b083b/04088/tutorial_html_9b9f57dc81561ae7.png 1532w"],
                "sizes": "(max-width: 1152px) 100vw, 1152px",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                },
                "loading": "lazy"
              }}></img>{`
    `}</span>
          </li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Navigate to the `}{`\``}{`Security`}{`\``}{` section and check the `}{`\``}<strong parentName="p">{`oauth-1
(OAuth)`}</strong>{`\``}{` checkbox. Make sure `}{`\``}{`inventory`}{`\``}{` is also checked.  `}</p>
        <span parentName="li" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "33.33333333333333%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAABYlAAAWJQFJUiTwAAAA50lEQVQoz5WRwW7DIAyGef09xd6hh533ArtV2q3bIVMqQgI4BAg0/MNE7WHrNNXSL+s31mcjC5pnrDEipYRHo1SlNUDRBc+vQDcCYtQWRDO894gV7JxruZSCbdv+Fr8zERvevwqeDgXSZIjT6QNSSgzDAKUUrLUta61hjGn+p1qdHNziUXLE8TPg5S0geAdxPvdtKyJqmZu7rrvB2e9gA6kmjONU/T6Mh06ThiONS7RYlgWi73cgiwvcyPle+Jixpnz3jb/fgDyFYXM9Dhd4K/a/DlD+OVBtaMAr7LolHyeEgJzzw2LgN+aWHyNaUbNhAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "tutorial html c8a8e86664fd2a1",
            "title": "tutorial html c8a8e86664fd2a1",
            "src": "/cp4i-demohub/static/87783ab303ac4efaf070b255570e9829/3cbba/tutorial_html_c8a8e86664fd2a1.png",
            "srcSet": ["/cp4i-demohub/static/87783ab303ac4efaf070b255570e9829/7fc1e/tutorial_html_c8a8e86664fd2a1.png 288w", "/cp4i-demohub/static/87783ab303ac4efaf070b255570e9829/a5df1/tutorial_html_c8a8e86664fd2a1.png 576w", "/cp4i-demohub/static/87783ab303ac4efaf070b255570e9829/3cbba/tutorial_html_c8a8e86664fd2a1.png 1152w", "/cp4i-demohub/static/87783ab303ac4efaf070b255570e9829/0b124/tutorial_html_c8a8e86664fd2a1.png 1728w", "/cp4i-demohub/static/87783ab303ac4efaf070b255570e9829/a9d85/tutorial_html_c8a8e86664fd2a1.png 2138w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Save your changes.`}</p>
      </li>
    </ol>
    <h2>{`Summary`}</h2>
    <p>{`You completed the APIC Dev Jam Lab 3 - Add OAuth Security to your
API. Throughout the tutorial, you explored the key takeaways:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Configure an OAuth 2.0 service, the Resource Owner Password grant
type.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Clone a new version of an API.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Secure the new version of your API.`}</p>
      </li>
    </ul>
    <p>{`Continue the APIC Dev Jam! Go To `}<a parentName="p" {...{
        "href": "../Lab4"
      }}>{`APIC (v10) Dev Jam Lab 4 - Use
Lifecycle controls to version your
API`}</a>{` to
manage the lifecycle of this API and test your new OAuth secured API.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      